/**
 * Web facade over `SessionTelemetry` for the non-Effect call sites (the route
 * guard in `routes/app/_authenticated.tsx`, the logout mutation in
 * `hooks/login-logout.hook.ts`). Adds the platform wiring the shared service
 * deliberately doesn't own: the Sentry breadcrumb and the web Analytics layer.
 * Mobile's counterpart is `apps/mobile/src/lib/sentry.ts`.
 */
import * as Sentry from '@sentry/react';
import { Effect, Layer, ManagedRuntime } from 'effect';
import { globalValue } from '@listening/utils/global-value';
import { AnalyticsWebLive } from '@listening/frontend/analytics/client-effect';
import { isForcedLogoutCause } from '@listening/frontend/analytics/session-end';
import {
  SessionTelemetry,
  type SessionEndInput,
} from '@listening/frontend/analytics/session-telemetry';

// A dedicated runtime rather than `clientRuntime`: these entry points also run
// during SSR (`beforeLoad`), where the client runtime doesn't belong — there
// the storage boot slot is a no-op, so nothing is ever reported. The layer is
// fully synchronous, so `runSync` is safe, and one runtime per page load is
// exactly the intended lifetime of the de-dupe Ref inside the service.
const telemetryRuntime = globalValue('Listening/SessionTelemetryRuntime', () =>
  ManagedRuntime.make(
    SessionTelemetry.layer.pipe(Layer.provide(AnalyticsWebLive)),
  ),
);

/** Call whenever an authenticated user is observed. */
export function rememberActiveSession(): void {
  telemetryRuntime.runSync(
    Effect.flatMap(SessionTelemetry, (s) => s.rememberActiveSession),
  );
}

/**
 * Record that the session ended. Explicit sign-outs are tagged for Sentry
 * context; only forced logouts become analytics events — see
 * `SessionTelemetry` for the reporting rules.
 */
export function markWebSessionEnd(input: SessionEndInput): void {
  Sentry.addBreadcrumb({
    category: 'auth.session',
    message: input.cause,
    level: isForcedLogoutCause(input.cause) ? 'warning' : 'info',
  });
  telemetryRuntime.runSync(
    Effect.flatMap(SessionTelemetry, (s) => s.markSessionEnd(input)),
  );
}
